import {
  PANEL_DESCRIPTIONS,
  PANEL_TITLES,
  type PanelId,
} from '@trajectory/ui';

export type GlossaryCategory =
  | 'Mission'
  | 'Orbit'
  | 'Vehicle'
  | 'Atmosphere'
  | 'Simulation'
  | 'Analysis';

export interface GlossaryEntry {
  id: string;
  term: string;
  abbreviation?: string;
  category: GlossaryCategory;
  definition: string;
  detail: string;
  unit?: string;
}

/**
 * The shared aerospace vocabulary for learner tooltips and the searchable
 * learning centre. Definitions intentionally distinguish physical quantities
 * from the approximations the application uses to estimate them.
 */
export const GLOSSARY: readonly GlossaryEntry[] = [
  {
    id: 'altitude',
    term: 'Altitude',
    category: 'Mission',
    definition: 'Height above the reference body’s surface.',
    detail:
      'Altitude is not distance from the body’s centre. In this app Earth altitude uses an oblate reference ellipsoid.',
    unit: 'm or km',
  },
  {
    id: 'azimuth',
    term: 'Launch azimuth',
    category: 'Mission',
    definition: 'The initial compass direction of flight, measured clockwise from north.',
    detail:
      'Azimuth helps determine the reachable orbit inclination and must respect the launch site’s safe flight corridor.',
    unit: 'degrees',
  },
  {
    id: 'utc',
    term: 'Coordinated Universal Time',
    abbreviation: 'UTC',
    category: 'Mission',
    definition: 'The world time standard used to remove local time-zone ambiguity.',
    detail:
      'Launch epoch affects Earth rotation, sunlight, planetary positions, and the orientation of an inertial orbit.',
  },
  {
    id: 'payload',
    term: 'Payload',
    category: 'Mission',
    definition: 'The spacecraft, cargo, people, and instruments the rocket is meant to deliver.',
    detail:
      'Rocket structure, tanks, engines, propellant, and payload fairing are vehicle mass rather than payload mass.',
    unit: 'kg',
  },
  {
    id: 'orbit',
    term: 'Orbit',
    category: 'Orbit',
    definition: 'Continuous free-fall around a body with enough sideways speed to keep missing it.',
    detail:
      'Reaching space is not the same as reaching orbit. A stable low Earth orbit needs roughly 7.8 km/s of horizontal speed.',
  },
  {
    id: 'leo',
    term: 'Low Earth orbit',
    abbreviation: 'LEO',
    category: 'Orbit',
    definition: 'An Earth orbit below roughly 2,000 km altitude.',
    detail:
      'LEO is used by the ISS, Earth-observation spacecraft, and many communications constellations.',
  },
  {
    id: 'meo',
    term: 'Medium Earth orbit',
    abbreviation: 'MEO',
    category: 'Orbit',
    definition: 'An Earth orbit above LEO and below geostationary altitude.',
    detail: 'Navigation constellations commonly operate in MEO.',
  },
  {
    id: 'geo',
    term: 'Geostationary orbit',
    abbreviation: 'GEO',
    category: 'Orbit',
    definition: 'A circular equatorial orbit whose period matches Earth’s rotation.',
    detail:
      'At about 35,786 km altitude, a GEO spacecraft appears fixed over one longitude.',
  },
  {
    id: 'periapsis',
    term: 'Periapsis',
    category: 'Orbit',
    definition: 'The closest point of an orbit to its central body.',
    detail:
      'For Earth this is also called perigee. A periapsis inside the atmosphere or surface is not a stable orbit.',
    unit: 'm or km altitude',
  },
  {
    id: 'apoapsis',
    term: 'Apoapsis',
    category: 'Orbit',
    definition: 'The farthest point of a bound orbit from its central body.',
    detail:
      'For Earth this is also called apogee. Escape trajectories do not have a finite apoapsis.',
    unit: 'm or km altitude',
  },
  {
    id: 'semi-major-axis',
    term: 'Semi-major axis',
    category: 'Orbit',
    definition: 'Half the longest diameter of an orbital ellipse.',
    detail:
      'Together with the central body’s gravity, it sets orbital energy and period.',
    unit: 'm',
  },
  {
    id: 'eccentricity',
    term: 'Eccentricity',
    category: 'Orbit',
    definition: 'A dimensionless measure of how non-circular an orbit is.',
    detail:
      'Zero is circular, values between zero and one are elliptical, one is parabolic escape, and values above one are hyperbolic.',
  },
  {
    id: 'inclination',
    term: 'Inclination',
    category: 'Orbit',
    definition: 'The tilt of an orbit relative to the central body’s equator.',
    detail:
      'Zero degrees is equatorial prograde, 90 degrees is polar, and values above 90 degrees are retrograde.',
    unit: 'degrees',
  },
  {
    id: 'raan',
    term: 'Right ascension of the ascending node',
    abbreviation: 'RAAN',
    category: 'Orbit',
    definition: 'The inertial direction where an orbit crosses north through the equatorial plane.',
    detail:
      'RAAN rotates the orbital plane around the central body’s spin axis.',
    unit: 'degrees',
  },
  {
    id: 'argument-of-periapsis',
    term: 'Argument of periapsis',
    category: 'Orbit',
    definition: 'The angle within the orbital plane from the ascending node to periapsis.',
    detail: 'It controls where the low point of an elliptical orbit lies in its plane.',
    unit: 'degrees',
  },
  {
    id: 'true-anomaly',
    term: 'True anomaly',
    category: 'Orbit',
    definition: 'The spacecraft’s current angular position measured from periapsis.',
    detail: 'True anomaly locates the vehicle on the orbit at the chosen epoch.',
    unit: 'degrees',
  },
  {
    id: 'delta-v',
    term: 'Delta-v',
    abbreviation: 'ΔV',
    category: 'Vehicle',
    definition: 'The ideal velocity change available from propulsion or required by a maneuver.',
    detail:
      'A launch vehicle spends delta-v on useful velocity plus gravity, drag, steering, and other losses. It is a budget, not simply final speed.',
    unit: 'm/s or km/s',
  },
  {
    id: 'thrust',
    term: 'Thrust',
    category: 'Vehicle',
    definition: 'The force produced by an engine.',
    detail:
      'Vacuum thrust differs from sea-level thrust because ambient pressure changes nozzle performance.',
    unit: 'N or kN',
  },
  {
    id: 'thrust-to-weight',
    term: 'Thrust-to-weight ratio',
    abbreviation: 'T/W',
    category: 'Vehicle',
    definition: 'Thrust divided by the vehicle’s weight in the local gravity field.',
    detail:
      'A vertical launch normally needs liftoff T/W above 1. Higher values accelerate faster but may increase structural and crew loads.',
  },
  {
    id: 'specific-impulse',
    term: 'Specific impulse',
    abbreviation: 'Isp',
    category: 'Vehicle',
    definition: 'A measure of rocket-engine propellant efficiency.',
    detail:
      'Higher Isp means more impulse from each unit of propellant weight. It is conventionally reported in seconds.',
    unit: 's',
  },
  {
    id: 'dry-mass',
    term: 'Dry mass',
    category: 'Vehicle',
    definition: 'Vehicle mass excluding usable propellant.',
    detail: 'Structure, engines, tanks, avionics, fairings, and payload contribute to dry mass.',
    unit: 'kg',
  },
  {
    id: 'propellant',
    term: 'Propellant',
    category: 'Vehicle',
    definition: 'Fuel and oxidizer expelled by a rocket engine to produce thrust.',
    detail:
      'The selected engine’s propellant type and efficiency affect mass flow and achievable delta-v.',
    unit: 'kg',
  },
  {
    id: 'staging',
    term: 'Staging',
    category: 'Vehicle',
    definition: 'Discarding empty vehicle hardware during flight.',
    detail:
      'Staging improves mass ratio. Separation timing and ignition delays can materially change the trajectory.',
  },
  {
    id: 'fairing',
    term: 'Payload fairing',
    category: 'Vehicle',
    definition: 'The aerodynamic shell protecting the payload during ascent.',
    detail:
      'Fairings are normally jettisoned after the atmosphere becomes thin enough, reducing inert mass.',
  },
  {
    id: 'dynamic-pressure',
    term: 'Dynamic pressure',
    abbreviation: 'q',
    category: 'Atmosphere',
    definition: 'A measure of aerodynamic loading equal to one half density times air-relative speed squared.',
    detail:
      'The point of maximum dynamic pressure is Max Q. It often drives throttle and structural constraints.',
    unit: 'Pa',
  },
  {
    id: 'mach',
    term: 'Mach number',
    category: 'Atmosphere',
    definition: 'Air-relative speed divided by the local speed of sound.',
    detail:
      'Mach 1 is locally sonic. The speed of sound changes with atmospheric temperature.',
  },
  {
    id: 'drag',
    term: 'Aerodynamic drag',
    category: 'Atmosphere',
    definition: 'Force opposing motion relative to the atmosphere.',
    detail:
      'Drag depends on density, relative speed, reference area, and drag coefficient.',
    unit: 'N',
  },
  {
    id: 'angle-of-attack',
    term: 'Angle of attack',
    abbreviation: 'AoA',
    category: 'Atmosphere',
    definition: 'The angle between the vehicle’s pointing direction and its air-relative velocity.',
    detail:
      'Large AoA can create high lift and side loads; launch vehicles usually keep it small during dense-atmosphere flight.',
    unit: 'degrees',
  },
  {
    id: 'g-load',
    term: 'G-load',
    category: 'Atmosphere',
    definition: 'Sensed acceleration expressed relative to standard Earth gravity.',
    detail:
      'It describes the load felt by the vehicle and occupants, not the gravitational field alone.',
    unit: 'g₀',
  },
  {
    id: 'ballistic-coefficient',
    term: 'Ballistic coefficient',
    category: 'Atmosphere',
    definition: 'Mass divided by drag coefficient and reference area.',
    detail:
      'A high ballistic coefficient penetrates deeper before slowing; a low value decelerates higher in the atmosphere.',
    unit: 'kg/m²',
  },
  {
    id: 'lift-to-drag',
    term: 'Lift-to-drag ratio',
    abbreviation: 'L/D',
    category: 'Atmosphere',
    definition: 'Aerodynamic lift divided by drag.',
    detail:
      'During entry, nonzero L/D allows crossrange and downrange control compared with a purely ballistic path.',
  },
  {
    id: 'heat-flux',
    term: 'Heat flux',
    category: 'Atmosphere',
    definition: 'Thermal power delivered per unit surface area.',
    detail:
      'Peak heat flux drives thermal-protection intensity, while integrated heat load drives total absorbed energy.',
    unit: 'W/m²',
  },
  {
    id: 'reference-frame',
    term: 'Reference frame',
    category: 'Simulation',
    definition: 'The coordinate system relative to which position and velocity are measured.',
    detail:
      'The app changes among Earth-fixed, Earth-centred inertial, body-centred, and heliocentric frames as a mission progresses.',
  },
  {
    id: 'inertial-frame',
    term: 'Inertial frame',
    category: 'Simulation',
    definition: 'A non-rotating coordinate frame suitable for applying Newton’s laws.',
    detail:
      'Earth rotates beneath an Earth-centred inertial trajectory, producing the ground track seen on the map.',
  },
  {
    id: 'ground-track',
    term: 'Ground track',
    category: 'Simulation',
    definition: 'The path directly beneath a spacecraft on a rotating body.',
    detail:
      'It is a surface projection, not the vehicle’s three-dimensional flight path.',
  },
  {
    id: 'deterministic',
    term: 'Deterministic simulation',
    category: 'Simulation',
    definition: 'A simulation that produces the same result from the same inputs.',
    detail:
      'Determinism supports reproducible experiments, regression testing, and academic comparison.',
  },
  {
    id: 'time-step',
    term: 'Integration time step',
    category: 'Simulation',
    definition: 'The simulated time advanced by each numerical propagation step.',
    detail:
      'Smaller steps can improve numerical accuracy but take more computation. Step size is not a measurement uncertainty.',
    unit: 's',
  },
  {
    id: 'j2',
    term: 'J2 perturbation',
    category: 'Simulation',
    definition: 'The leading correction for a planet’s equatorial bulge.',
    detail:
      'J2 causes effects such as nodal precession that an ideal two-body orbit cannot reproduce.',
  },
  {
    id: 'third-body',
    term: 'Third-body gravity',
    category: 'Simulation',
    definition: 'Gravity from another body in addition to the primary central body.',
    detail:
      'Sun and Moon perturbations become important for long-duration and high-altitude trajectories.',
  },
  {
    id: 'solar-radiation-pressure',
    term: 'Solar radiation pressure',
    abbreviation: 'SRP',
    category: 'Simulation',
    definition: 'A small force produced when sunlight transfers momentum to a spacecraft.',
    detail:
      'SRP matters most for high area-to-mass spacecraft and is removed while the vehicle is in eclipse.',
  },
  {
    id: 'hohmann-transfer',
    term: 'Hohmann transfer',
    category: 'Analysis',
    definition: 'A two-burn transfer between coplanar circular orbits using half an ellipse.',
    detail:
      'It is fuel-efficient for many radius changes but does not automatically account for launch windows, finite burns, or plane changes.',
  },
  {
    id: 'lambert',
    term: 'Lambert transfer',
    category: 'Analysis',
    definition: 'An orbit connecting two positions in a specified time of flight.',
    detail:
      'Lambert targeting is widely used for preliminary interplanetary and rendezvous design.',
  },
  {
    id: 'c3',
    term: 'Characteristic energy',
    abbreviation: 'C3',
    category: 'Analysis',
    definition: 'Hyperbolic excess speed squared, used to describe launch escape energy.',
    detail:
      'C3 is zero at parabolic escape and positive for a hyperbolic departure.',
    unit: 'km²/s²',
  },
  {
    id: 'phase-angle',
    term: 'Departure phase angle',
    category: 'Analysis',
    definition: 'The angular separation between departure and destination bodies at departure.',
    detail: 'A transfer window occurs when the bodies have the geometry required by the selected trajectory.',
    unit: 'degrees',
  },
  {
    id: 'gravity-loss',
    term: 'Gravity loss',
    category: 'Analysis',
    definition: 'Delta-v spent counteracting gravity instead of increasing useful orbital energy.',
    detail:
      'A very slow vertical ascent increases gravity loss; an overly aggressive turn can increase drag and structural load.',
    unit: 'm/s',
  },
  {
    id: 'drag-loss',
    term: 'Drag loss',
    category: 'Analysis',
    definition: 'Delta-v dissipated by aerodynamic drag.',
    detail:
      'Drag loss is concentrated in the lower atmosphere and rises strongly with air-relative speed.',
    unit: 'm/s',
  },
  {
    id: 'optimization',
    term: 'Numerical optimization',
    category: 'Analysis',
    definition: 'Systematically searching input values for a better objective score.',
    detail:
      'The optimizer is deterministic numerical software, not generative AI. Its result is only as valid as the model, bounds, and objective.',
  },
  {
    id: 'convergence',
    term: 'Convergence',
    category: 'Analysis',
    definition: 'The process of an optimization or numerical method settling toward a stable result.',
    detail:
      'A flat convergence curve can mean a good solution, weak variables, restrictive bounds, or a local optimum.',
  },
  {
    id: 'margin',
    term: 'Performance margin',
    category: 'Analysis',
    definition: 'Available capability minus estimated requirement.',
    detail:
      'Positive delta-v margin is useful screening evidence, not proof that all trajectory, structural, thermal, and operational constraints are satisfied.',
  },
] as const;

export interface PanelGuide {
  summary: string;
  firstAction: string;
  learnerTip: string;
  professionalTip: string;
}

export const PANEL_GUIDES: Record<PanelId, PanelGuide> = {
  'mission-explorer': {
    summary: PANEL_DESCRIPTIONS['mission-explorer'],
    firstAction: 'Open the demo mission or use + to start the guided mission wizard.',
    learnerTip: 'Begin with a simple LEO or suborbital mission and change one variable at a time.',
    professionalTip: 'Use tags and version history to separate baselines, trades, and sensitivity cases.',
  },
  'rocket-editor': {
    summary: PANEL_DESCRIPTIONS['rocket-editor'],
    firstAction: 'Clone a reference vehicle, link it to the mission, then check liftoff T/W and delta-v.',
    learnerTip: 'Watch how propellant mass and staging change delta-v through the rocket equation.',
    professionalTip: 'Treat catalogue engines and cost values as preliminary design inputs and verify source data externally.',
  },
  'orbit-editor': {
    summary: PANEL_DESCRIPTIONS['orbit-editor'],
    firstAction: 'Choose a preset, then vary periapsis, apoapsis, and inclination.',
    learnerTip: 'Start with apsides and inclination before editing orientation angles such as RAAN.',
    professionalTip: 'Element edits are osculating initial conditions at the mission epoch; long-term propagation includes configured perturbations.',
  },
  'viewport-3d': {
    summary: PANEL_DESCRIPTIONS['viewport-3d'],
    firstAction: 'Run a simulation, follow the vehicle, and toggle trajectory layers.',
    learnerTip: 'Compare the inertial path with the rotating surface ground track.',
    professionalTip: 'Use the view for qualitative geometry checks; use exported numerical telemetry for quantitative work.',
  },
  'map-2d': {
    summary: PANEL_DESCRIPTIONS['map-2d'],
    firstAction: 'Run a simulation, play it, then click the trajectory to seek.',
    learnerTip: 'Notice how Earth rotation moves the ground track beneath an inertial orbit.',
    professionalTip: 'Confirm the frame label before interpreting distances in lunar or heliocentric phases.',
  },
  charts: {
    summary: PANEL_DESCRIPTIONS.charts,
    firstAction: 'Use a quick-start run or configure a mission and select Run full mission.',
    learnerTip: 'Relate Max Q, throttle, Mach, and G-load to the phases shown on the timeline.',
    professionalTip: 'Export telemetry for independent analysis, uncertainty studies, and reproducible academic figures.',
  },
  timeline: {
    summary: PANEL_DESCRIPTIONS.timeline,
    firstAction: 'Play or drag the scrubber and select an event marker.',
    learnerTip: 'Events explain why curves change—look for staging, cutoff, insertion, and encounter events.',
    professionalTip: 'Use high playback multipliers for coast phases and one-second stepping near burns.',
  },
  inspector: {
    summary: PANEL_DESCRIPTIONS.inspector,
    firstAction: 'Select a mission, stage, or maneuver in another panel.',
    learnerTip: 'Descriptions under fields explain units and physical meaning in Guided mode.',
    professionalTip: 'Version history creates a new version on restore, preserving an auditable chain.',
  },
  analytics: {
    summary: PANEL_DESCRIPTIONS.analytics,
    firstAction: 'Link a rocket and run the mission before interpreting margins.',
    learnerTip: 'A green score does not replace checking each constraint and the actual trajectory.',
    professionalTip: 'Cost and probability fields are screening estimates, not quoted prices or certified reliability.',
  },
  optimizer: {
    summary: PANEL_DESCRIPTIONS.optimizer,
    firstAction: 'Choose an objective and a small set of variables, then run the optimizer.',
    learnerTip: 'Change two or three variables first so you can understand the convergence.',
    professionalTip: 'Re-run the full simulation after applying parameters and validate constraints independently.',
  },
  'transfer-planner': {
    summary: PANEL_DESCRIPTIONS['transfer-planner'],
    firstAction: 'Set a departure orbit and choose orbit-to-orbit or destination-body analysis.',
    learnerTip: 'Compare required delta-v and transfer time before changing the departure altitude.',
    professionalTip: 'Results are preliminary impulsive-transfer estimates; operational targeting needs ephemeris, covariance, finite-burn, and navigation analysis.',
  },
};

export interface OperationGuide {
  id: string;
  title: string;
  description: string;
  effect: string;
  reversible: boolean;
}

export const OPERATION_GUIDES: readonly OperationGuide[] = [
  {
    id: 'create-mission',
    title: 'Create mission',
    description: 'Creates a persisted mission document from the six-step wizard.',
    effect: 'The mission is selected and becomes available to editors and simulation; it can be exported or deleted later.',
    reversible: true,
  },
  {
    id: 'edit-document',
    title: 'Edit mission or rocket',
    description: 'Changes the current document and marks it dirty.',
    effect: 'Autosave writes after the debounce; optimistic version checks prevent silent overwrites.',
    reversible: true,
  },
  {
    id: 'run-simulation',
    title: 'Run full mission',
    description: 'Sends a snapshot of the current mission and vehicle to a browser Web Worker.',
    effect: 'The latest completed run replaces displayed telemetry; mission and rocket documents are not changed.',
    reversible: true,
  },
  {
    id: 'quick-start',
    title: 'Run quick-start simulation',
    description: 'Runs a built-in demonstration vehicle and launch profile.',
    effect: 'It replaces displayed telemetry but does not modify the open mission or rocket.',
    reversible: true,
  },
  {
    id: 'apply-optimizer',
    title: 'Apply optimizer result',
    description: 'Copies the completed ascent program into the mission that launched the job.',
    effect: 'The mission becomes dirty and must be simulated again to validate the result.',
    reversible: true,
  },
  {
    id: 'restore-version',
    title: 'Restore mission version',
    description: 'Copies a historical snapshot into a new current version.',
    effect: 'History remains intact; the older record is never overwritten.',
    reversible: true,
  },
  {
    id: 'export',
    title: 'Export mission',
    description: 'Downloads the selected mission as JSON, CSV, or a PDF summary.',
    effect: 'No mission data is changed.',
    reversible: true,
  },
  {
    id: 'delete',
    title: 'Delete mission or rocket',
    description: 'Permanently removes the selected persisted record after confirmation.',
    effect: 'Deletion is not reversible from the interface; export important work first.',
    reversible: false,
  },
] as const;

export function searchGlossary(query: string): readonly GlossaryEntry[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return GLOSSARY;
  return GLOSSARY.filter((entry) =>
    [
      entry.term,
      entry.abbreviation,
      entry.category,
      entry.definition,
      entry.detail,
      entry.unit,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(normalized),
  );
}

export function glossaryEntry(id: string): GlossaryEntry | undefined {
  return GLOSSARY.find((entry) => entry.id === id);
}

export const panelGuideList = (Object.keys(PANEL_GUIDES) as PanelId[]).map((id) => ({
  id,
  title: PANEL_TITLES[id],
  ...PANEL_GUIDES[id],
}));
